import React, { useState } from 'react';
import { Check, ChevronDown, Package, RotateCcw, UserRound, X } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../../ui/command';

// ORDER-FILTERS-001: compact searchable dropdown (Client Code / Product) — TL dashboards only.
const ScopeCombobox = ({ label, icon: Icon, value, options, onChange, placeholder, testId, width }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className={`relative ${width}`} data-testid={`${testId}-wrapper`}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            role="combobox"
            aria-expanded={open}
            aria-label={label}
            data-testid={testId}
            className={`flex h-9 w-full items-center gap-2 rounded-lg border bg-white pl-3 pr-2 text-sm shadow-sm transition-colors hover:border-slate-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-300 ${value ? 'border-blue-200' : 'border-slate-200'}`}
          >
            <Icon className="h-4 w-4 shrink-0 text-slate-400" />
            <span className={`flex-1 truncate text-left ${value ? 'font-semibold text-slate-800' : 'text-slate-400'}`} data-testid={`${testId}-value`}>
              {value || placeholder}
            </span>
            {value && <span className="w-5" aria-hidden="true" />}
            <ChevronDown className="h-4 w-4 shrink-0 text-slate-400" />
          </button>
        </PopoverTrigger>
        {value && (
          <button
            type="button"
            aria-label={`Clear ${label}`}
            data-testid={`${testId}-clear`}
            onClick={() => onChange('')}
            className="absolute right-8 top-1/2 -translate-y-1/2 rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
        <PopoverContent className="w-[280px] p-0" align="start">
          <Command>
            <CommandInput placeholder={`Search ${label.toLowerCase()}…`} className="h-9" data-testid={`${testId}-search`} />
            <CommandList className="max-h-64">
              <CommandEmpty>No matches.</CommandEmpty>
              <CommandGroup>
                {options.map((opt) => (
                  <CommandItem
                    key={opt.value}
                    value={opt.value}
                    data-testid={`${testId}-option`}
                    onSelect={() => { onChange(opt.value === value ? '' : opt.value); setOpen(false); }}
                    className="flex items-center gap-2 text-sm"
                  >
                    <Check className={`h-4 w-4 ${opt.value === value ? 'opacity-100 text-blue-600' : 'opacity-0'}`} />
                    <span className="flex-1 truncate">{opt.value}</span>
                    <span className="text-xs tabular-nums text-slate-400">{opt.count}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};

// `options` = facets returned by the SAME order-list request that filled the table
// ({ client_codes: [{value,count}], products: [{value,count}] }) — counts always match the rows.
export const OrderScopeFilters = ({ clientCode, clientProduct, onChange, options }) => (
  <>
    <ScopeCombobox label="Client Code" icon={UserRound} value={clientCode} options={options?.client_codes || []}
      onChange={(v) => onChange({ client_code: v })} placeholder="All clients" testId="filter-client-code" width="w-[190px]" />
    <ScopeCombobox label="Product" icon={Package} value={clientProduct} options={options?.products || []}
      onChange={(v) => onChange({ client_product: v })} placeholder="All products" testId="filter-product" width="w-[220px]" />
  </>
);

export const OrderScopeChips = ({ clientCode, clientProduct, onChange }) => {
  if (!clientCode && !clientProduct) return null;
  const Chip = ({ label, value, onRemove, testId }) => (
    <span className="inline-flex items-center gap-1.5 rounded-lg bg-blue-50 px-3 py-1.5 text-sm font-medium text-blue-700 ring-1 ring-blue-100" data-testid={testId}>
      {label}: {value}
      <button type="button" aria-label={`Remove ${label} filter`} onClick={onRemove} className="rounded p-0.5 text-blue-500 hover:bg-blue-100 hover:text-blue-800" data-testid={`${testId}-remove`}>
        <X className="h-3.5 w-3.5" />
      </button>
    </span>
  );
  return (
    <div className="flex items-center gap-2" data-testid="scope-filter-chips">
      {clientCode && <Chip label="Client" value={clientCode} testId="chip-client-code" onRemove={() => onChange({ client_code: '' })} />}
      {clientProduct && <Chip label="Product" value={clientProduct} testId="chip-product" onRemove={() => onChange({ client_product: '' })} />}
      <button
        type="button"
        onClick={() => onChange({ client_code: '', client_product: '' })}
        className="ml-auto inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-800"
        data-testid="scope-filters-clear-all"
      >
        <RotateCcw className="h-4 w-4" /> Clear All
      </button>
    </div>
  );
};

export default OrderScopeFilters;
